import React, { useState, useRef, useEffect } from 'react';

const WORKER_URL = 'https://whisper-worker.speedofmastry.workers.dev';

function App() {
  const [isRecording, setIsRecording] = useState(false);
  const [transcription, setTranscription] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [audioUrl, setAudioUrl] = useState('');
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);

  // Debug effect to log state changes
  useEffect(() => {
    console.log('App state changed:', {
      isRecording,
      transcription: transcription ? 'has transcription' : 'no transcription',
      isProcessing,
      error,
      audioFile: audioFile ? 'has file' : 'no file'
    });
  }, [isRecording, transcription, isProcessing, error, audioFile]);

  const startRecording = async () => {
    console.log('Starting recording...');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        console.log('Data available:', event.data.size, 'bytes');
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        console.log('Recording stopped, processing...');
        try {
          const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
          console.log('Audio blob created:', audioBlob.size, 'bytes');
          
          // Use setTimeout to prevent blocking the UI
          setTimeout(() => {
            transcribeAudio(audioBlob).catch(err => {
              console.error('Error in transcribeAudio:', err);
              setError('Error processing recording: ' + err.message);
              setIsProcessing(false);
            });
          }, 100);
          
        } catch (err) {
          console.error('Error in recording onstop:', err);
          setError('Error processing recording: ' + err.message);
          setIsProcessing(false);
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setError('');
      console.log('Recording started successfully');
    } catch (err) {
      console.error('Error starting recording:', err);
      setError('Error accessing microphone: ' + err.message);
    }
  };

  const stopRecording = () => {
    console.log('Stopping recording...');
    try {
      if (mediaRecorderRef.current && isRecording) {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        setIsRecording(false);
        console.log('Recording stopped successfully');
      }
    } catch (err) {
      console.error('Error stopping recording:', err);
      setError('Error stopping recording: ' + err.message);
      setIsRecording(false);
    }
  };

  const handleFileUpload = (event) => {
    console.log('File upload event:', event.target.files[0]);
    try {
      const file = event.target.files[0];
      if (file) {
        setAudioFile(file);
        setAudioUrl(URL.createObjectURL(file));
        setError('');
        console.log('File uploaded:', file.name, file.size);
      }
    } catch (err) {
      console.error('Error handling file upload:', err);
      setError('Error uploading file: ' + err.message);
    }
  };

  const transcribeAudio = async (audioBlob) => {
    console.log('=== TRANSCRIPTION START ===');
    console.log('Audio blob size:', audioBlob.size);
    
    // Set processing state first
    setIsProcessing(true);
    setError('');
    
    try {
      // Validate audio blob
      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('Invalid audio data');
      }

      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.wav');

      console.log('Sending request to:', `${WORKER_URL}/transcribe`);
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
      
      const response = await fetch(`${WORKER_URL}/transcribe`, {
        method: 'POST',
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      console.log('Response received:', response.status, response.statusText);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Response data:', data);
      
      if (data && data.success) {
        console.log('Setting transcription:', data.transcription);
        setTranscription(data.transcription || '');
        console.log('Transcription set successfully');
      } else {
        console.error('Transcription failed:', data?.error || 'Unknown error');
        setError(data?.error || 'Transcription failed');
      }
    } catch (err) {
      console.error('Error transcribing audio:', err);
      if (err.name === 'AbortError') {
        setError('Transcription timed out. Please try again.');
      } else {
        setError('Error transcribing audio: ' + err.message);
      }
    } finally {
      console.log('Setting isProcessing to false');
      setIsProcessing(false);
      console.log('=== TRANSCRIPTION END ===');
    }
  };

  const handleTranscribeFile = async () => {
    console.log('Handling file transcription...');
    try {
      if (!audioFile) {
        setError('Please select an audio file first');
        return;
      }
      await transcribeAudio(audioFile);
    } catch (err) {
      console.error('Error in handleTranscribeFile:', err);
      setError('Error processing file: ' + err.message);
      setIsProcessing(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(transcription);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
      setError('Failed to copy to clipboard');
    }
  };

  const downloadTranscription = () => {
    try {
      const blob = new Blob([transcription], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'transcription.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading transcription:', err);
      setError('Failed to download transcription');
    }
  };

  const clearAll = () => {
    console.log('Clearing all data...');
    try {
      setTranscription('');
      setAudioFile(null);
      setAudioUrl('');
      setError('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      console.log('All data cleared');
    } catch (err) {
      console.error('Error clearing all:', err);
    }
  };

  console.log('Rendering App component...');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            🎤 Whisper STT
          </h1>
          <p className="text-lg text-gray-600">
            Speech-to-Text powered by OpenAI Whisper
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="space-y-6">
            {/* File Upload Card */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                📁 Upload Audio File
              </h2>
              
              <div className="space-y-4">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/*"
                  onChange={handleFileUpload}
                  className="input-field"
                />
                
                {audioFile && (
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <span>📄 {audioFile.name}</span>
                    <span className="text-gray-400">
                      ({(audioFile.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                )}
                
                <button
                  onClick={handleTranscribeFile}
                  disabled={!audioFile || isProcessing}
                  className="btn-primary w-full flex items-center justify-center space-x-2"
                >
                  {isProcessing ? (
                    <>
                      <span>⏳ Processing...</span>
                    </>
                  ) : (
                    <>
                      <span>🎵 Transcribe File</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Recording Card */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                🎙️ Record Audio
              </h2>
              
              <div className="space-y-4">
                <div className="flex space-x-3">
                  <button
                    onClick={isRecording ? stopRecording : startRecording}
                    disabled={isProcessing}
                    className={`flex-1 flex items-center justify-center space-x-2 py-3 px-4 rounded-lg font-medium transition-all duration-200 ${
                      isRecording 
                        ? 'bg-red-600 hover:bg-red-700 text-white' 
                        : 'bg-blue-600 hover:bg-blue-700 text-white'
                    }`}
                  >
                    {isRecording ? (
                      <>
                        <span>⏹️ Stop Recording</span>
                      </>
                    ) : (
                      <>
                        <span>🎤 Start Recording</span>
                      </>
                    )}
                  </button>
                </div>
                
                {isRecording && (
                  <div className="flex items-center justify-center space-x-2 text-red-600">
                    <div className="w-3 h-3 bg-red-600 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium">Recording...</span>
                  </div>
                )}
              </div>
            </div>

            {/* Audio Preview */}
            {audioUrl && (
              <div className="card">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Audio Preview</h3>
                <audio controls className="w-full" src={audioUrl} />
                <button
                  onClick={clearAll}
                  className="btn-secondary w-full mt-3 flex items-center justify-center space-x-2"
                >
                  <span>🗑️ Clear All</span>
                </button>
              </div>
            )}
          </div>

          {/* Output Section */}
          <div className="space-y-6">
            {/* Transcription Card */}
            <div className="card">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                ✅ Transcription
              </h2>
              
              <div className="space-y-4">
                {isProcessing && (
                  <div className="flex items-center justify-center space-x-2 text-blue-600 py-8">
                    <span>⏳ Transcribing audio...</span>
                  </div>
                )}
                
                {transcription ? (
                  <div className="space-y-3">
                    <div className="bg-gray-50 rounded-lg p-4 min-h-[200px] max-h-[400px] overflow-y-auto">
                      <p className="text-gray-800 whitespace-pre-wrap">{transcription}</p>
                    </div>
                    
                    <div className="flex space-x-2">
                      <button
                        onClick={copyToClipboard}
                        className="btn-primary flex items-center space-x-2"
                      >
                        {copied ? (
                          <>
                            <span>✅ Copied!</span>
                          </>
                        ) : (
                          <>
                            <span>📋 Copy</span>
                          </>
                        )}
                      </button>
                      
                      <button
                        onClick={downloadTranscription}
                        className="btn-secondary flex items-center space-x-2"
                      >
                        <span>💾 Download</span>
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <span className="text-4xl mb-3 block">🎵</span>
                    <p>No transcription yet</p>
                    <p className="text-sm">Upload an audio file or start recording</p>
                  </div>
                )}
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="card border-red-200 bg-red-50">
                <div className="flex items-center space-x-2 text-red-700">
                  <span>⚠️</span>
                  <span className="font-medium">Error</span>
                </div>
                <p className="text-red-600 mt-2">{error}</p>
              </div>
            )}

            {/* API Status */}
            <div className="card bg-green-50 border-green-200">
              <div className="flex items-center space-x-2 text-green-700">
                <span>✅</span>
                <span className="font-medium">API Status</span>
              </div>
              <p className="text-green-600 mt-2">
                Connected to Whisper Worker at {WORKER_URL}
              </p>
            </div>

            {/* Debug Info */}
            <div className="card bg-gray-50 border-gray-200">
              <div className="flex items-center space-x-2 text-gray-700">
                <span>🔍</span>
                <span className="font-medium">Debug Info</span>
              </div>
              <div className="text-xs text-gray-600 mt-2 space-y-1">
                <p>Recording: {isRecording ? 'Yes' : 'No'}</p>
                <p>Processing: {isProcessing ? 'Yes' : 'No'}</p>
                <p>Has Transcription: {transcription ? 'Yes' : 'No'}</p>
                <p>Has Error: {error ? 'Yes' : 'No'}</p>
                <p>Has Audio File: {audioFile ? 'Yes' : 'No'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-12 text-gray-500">
          <p>Powered by OpenAI Whisper • Cloudflare Workers</p>
        </div>
      </div>
    </div>
  );
}

export default App;
